import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography
} from "@mui/material";
import * as S from "../styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import serviceRelatorio from "@/services/serviceRelatorio";
import { IDialogProps } from "./geral";
import { CAMPOS_DISPONIVEIS } from "./camposWhiteLabel";

export default function Parametrizado({ open, onClose }: IDialogProps) {
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [acao, setAcao] = useState<{ value: number; label: string } | null>({ value: 1, label: "Incluir" });
  const [relSelcted, setRelSelected] = useState<{ value: number; label: string } | null>(null)
  const [camposSelecionados, setCamposSelecionados] = useState<Record<string, boolean>>({});
  const [ordemSelecao, setOrdemSelecao] = useState<string[]>([]);
  const [buscaCampo, setBuscaCampo] = useState("");
  const [modelosData, setModelosData] = useState<Array<any>>([]);

  const options = [
    { value: 1, label: "Incluir" },
    { value: 2, label: "Editar" },
    { value: 3, label: "Excluir" },
  ];

  const [opt, setOpt] = useState<Array<{ value: number; label: string }>>([])

  const camposFiltrados = useMemo(() => {
    return CAMPOS_DISPONIVEIS.filter(campo => 
      campo.nome.toLowerCase().includes(buscaCampo.toLowerCase())
    );
  }, [buscaCampo]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const { data } = await serviceRelatorio.getRelatorioPersonalizado();
        const filteredData = data.filter((y) => y.habilitado.toLowerCase() === "sim");
        const newOptions = filteredData.map((x) => ({
          value: x.relatorioWhiteLabelID,
          label: x.nomeRelatorioWhiteLabel,
        }));
        if (isMounted) {
          setOpt(newOptions);
          setModelosData(filteredData);
        }
      } catch {
        if (isMounted) {
          setOpt([]);
          setModelosData([]);
        }
      }
    };

    const init = async () => {
      await fetchData();
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [])

  useEffect(() => {
    const camposIniciais: Record<string, boolean> = {};
    CAMPOS_DISPONIVEIS.forEach(campo => {
      camposIniciais[campo.id] = false;
    });
    setCamposSelecionados(camposIniciais);
    setOrdemSelecao([]);
  }, []);

  // Carregar campos do modelo selecionado quando estiver editando
  useEffect(() => {
    if (acao?.value === 2 && relSelcted && modelosData.length > 0) {
      const modeloSelecionado = modelosData.find(modelo => modelo.relatorioWhiteLabelID === relSelcted.value);
      if (modeloSelecionado) {
        // Carregar o título do modelo
        setTitulo(modeloSelecionado.nomeRelatorioWhiteLabel);
        
        // Carregar os campos do modelo (separados por ;)
        const camposDoModelo = modeloSelecionado.listaCampos ? modeloSelecionado.listaCampos.split(';') : [];
        
        // Resetar seleção
        const novosCamposSelecionados: Record<string, boolean> = {};
        CAMPOS_DISPONIVEIS.forEach(campo => {
          novosCamposSelecionados[campo.id] = camposDoModelo.includes(campo.id);
        });
        
        setCamposSelecionados(novosCamposSelecionados);
        setOrdemSelecao(camposDoModelo.filter(campo => 
          CAMPOS_DISPONIVEIS.some(disponivel => disponivel.id === campo)
        ));
      }
    }
  }, [acao, relSelcted, modelosData]);

  const onSubmit = () => {
    if (!acao) {
      toast.error("Por favor, selecione uma ação antes de continuar.");
      return;
    }

    if (acao.value === 1) {
      if (!titulo.trim()) {
        toast.error("Por favor, digite um nome para o modelo white label.");
        return;
      }
      
      const camposAtivos = ordemSelecao.filter(campoId => camposSelecionados[campoId]);
      if (camposAtivos.length === 0) {
        toast.error("Por favor, selecione ao menos um campo para o modelo white label.");
        return;
      }
    }

    if (acao.value === 2) {
      if (!relSelcted) {
        toast.error("Por favor, selecione um modelo white label para editar.");
        return;
      }
      
      if (!titulo.trim()) {
        toast.error("Por favor, digite um nome para o modelo white label.");
        return;
      }
      
      const camposAtivos = ordemSelecao.filter(campoId => camposSelecionados[campoId]);
      if (camposAtivos.length === 0) {
        toast.error("Por favor, selecione ao menos um campo para o modelo white label.");
        return;
      }
    }

    if (acao.value === 3) {
      if (!relSelcted) {
        toast.error("Por favor, selecione um modelo white label para excluir.");
        return;
      }
    }

    setDisabled(true);
    setLoading(true);

    const camposAtivos = ordemSelecao.filter(campoId => camposSelecionados[campoId]);

    const obj = {
      nomeRelatorioWhiteLabel: titulo,
      listaCampos: camposAtivos.join(";"),
      relatorioTipoID: 1,
      relatorioWhiteLabelID: acao.value === 2 ? relSelcted?.value || 0 : 0,
    };

    const request =
      acao.value === 1 || acao.value === 2
        ? serviceRelatorio.postRelatorioPersonalizado(obj)
        : acao.value === 3
          ? serviceRelatorio.deleteRelatorioPersonalizado(String(relSelcted?.value))
          : Promise.resolve();

    request
      .then(() => {
        if (acao.value === 1) {
          toast.success(`Modelo "${titulo}" criado com sucesso!`);
        } else if (acao.value === 2) {
          toast.success(`Modelo "${titulo}" atualizado com sucesso!`);
        } else if (acao.value === 3) {
          toast.success(`Modelo excluído com sucesso!`);
        }
        onClose();
      })
      .catch((error) => {
        console.error('Erro na operação:', error);
        if (acao.value === 1) {
          toast.error(`Erro ao criar modelo white label.`);
        } else if (acao.value === 2) {
          toast.error(`Erro ao atualizar modelo white label.`);
        } else if (acao.value === 3) {
          toast.error(`Erro ao excluir modelo white label.`);
        }
      })
      .finally(() => {
        setDisabled(false);
        setLoading(false);
      });
  };

  const getButtonLabel = () => {
    if (!acao) return "Salvar";
    if (acao.value === 1) return "Salvar";
    if (acao.value === 2) return "Alterar";
    if (acao.value === 3) return "Excluir";
    return "Salvar";
  };

  const resetAll = () => {
    setTitulo("");
    setAcao({ value: 1, label: "Incluir" });
    setRelSelected(null);
    setBuscaCampo("");
    setOrdemSelecao([]);
    const camposIniciais: Record<string, boolean> = {};
    CAMPOS_DISPONIVEIS.forEach(campo => {
      camposIniciais[campo.id] = false;
    });
    setCamposSelecionados(camposIniciais);
  };

  const handleClose = () => {
    resetAll();

    setTimeout(() => {
      document.getElementById("botao-foco-externo")?.focus();
    }, 0);

    onClose(); 
  };

  const toggleTodosCampos = (selecionar: boolean) => {
    setCamposSelecionados(prev => {
      const novosResultados = { ...prev };
      CAMPOS_DISPONIVEIS.forEach(campo => {
        novosResultados[campo.id] = selecionar;
      });
      return novosResultados;
    });
    
    if (selecionar) {
      setOrdemSelecao(CAMPOS_DISPONIVEIS.map(campo => campo.id));
    } else {
      setOrdemSelecao([]);
    }
  };

  
  const isAllSelected = () => {
    return CAMPOS_DISPONIVEIS.every(campo => camposSelecionados[campo.id]);
  };

  return (
    <Dialog maxWidth="md" fullWidth onClose={handleClose} open={open}>
      <DialogTitle>
        <S.Title>Gerenciar Modelos Personalizados</S.Title>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <DialogContent dividers>
          <S.Subtitles>Configuração</S.Subtitles>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={options}
                value={acao}
                onChange={(_, newValue) => setAcao(newValue)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    size="small"
                    label="Ação"
                    required
                    error={!acao}
                    helperText={!acao ? "Campo obrigatório" : ""}
                  />
                )}
              />
            </Grid>
            {(acao?.value !== 1) && (
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={opt}
                  value={relSelcted}
                  onChange={(_, newValue) => setRelSelected(newValue)}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      size="small"
                      label="Modelos Salvos"
                      placeholder="Selecione um modelo"
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
          
          {(acao?.label !== 'Excluir') && (
            <>
              <S.Subtitles>Informações do Modelo</S.Subtitles>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    margin="normal"
                    placeholder="Digite o título do modelo personalizado"
                    label="Título do Modelo"
                    fullWidth
                    required
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                  />
                </Grid>
              </Grid>
              
              <S.Subtitles style={{ marginBottom: "1rem" }}>Campos Disponíveis</S.Subtitles>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    margin="normal"
                    fullWidth
                    placeholder="Buscar campos..."
                    value={buscaCampo}
                    onChange={(e) => setBuscaCampo(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => toggleTodosCampos(!isAllSelected())}
                    variant="outlined"
                  >
                    {isAllSelected() ? 'Desmarcar Todos' : 'Marcar Todos'}
                  </Button>
                </Grid>
              </Grid>

              <Box 
                sx={{ 
                  height: '300px', 
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: 'background.paper'
                }}
              >
                {camposFiltrados.map((campo, index) => (
                  <Box
                    key={campo.id}
                    sx={{
                      borderBottom: index < camposFiltrados.length - 1 ? '1px solid #f0f0f0' : 'none',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={camposSelecionados[campo.id] || false}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setCamposSelecionados((prev) => ({
                              ...prev,
                              [campo.id]: isChecked,
                            }));
                            
                            setOrdemSelecao(prev => {
                              if (isChecked) {
                                return prev.includes(campo.id) ? prev : [...prev, campo.id];
                              } else {
                                return prev.filter(id => id !== campo.id);
                              }
                            });
                          }}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {campo.nome}
                        </Typography>
                      }
                      sx={{
                        margin: 0,
                        padding: '8px 12px',
                        width: '100%',
                        backgroundColor: camposSelecionados[campo.id] ? 'action.selected' : 'transparent',
                      }}
                    />
                  </Box>
                ))}
                
                {camposFiltrados.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum campo encontrado para "{buscaCampo}"
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
          <Button
            style={{ width: "159px", height: "44px" }}
            onClick={handleClose}
            color="warning"
            variant="contained"
          >
            Cancelar
          </Button>
          <LoadingButton
            loading={loading}
            disabled={disabled}
            style={{ width: "159px", height: "44px" }}
            type="submit"
            variant="contained"
          >
            {getButtonLabel()}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}